import { useState } from "react";
import { Construction, Wrench, KeyRound, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import { supabase } from "@/integrations/supabase/client";

const maintenanceTexts = {
  pl: { title: "Strona w przebudowie", desc: "Pracujemy nad ulepszeniami. Wrócimy wkrótce!", sorry: "Przepraszamy za utrudnienia.", working: "Trwają prace...", loginTitle: "Dostęp administratora", email: "Email", password: "Hasło", login: "Zaloguj", error: "Nieprawidłowe dane logowania", success: "Dostęp przyznany!" },
  en: { title: "Site Under Construction", desc: "We're working on improvements. We'll be back soon!", sorry: "Sorry for the inconvenience.", working: "Work in progress...", loginTitle: "Admin Access", email: "Email", password: "Password", login: "Log in", error: "Invalid credentials", success: "Access granted!" },
  fr: { title: "Site en construction", desc: "Nous travaillons sur des améliorations. Nous serons bientôt de retour !", sorry: "Désolé pour le désagrément.", working: "Travaux en cours...", loginTitle: "Accès admin", email: "Email", password: "Mot de passe", login: "Connexion", error: "Identifiants invalides", success: "Accès accordé !" },
  de: { title: "Seite im Umbau", desc: "Wir arbeiten an Verbesserungen. Wir sind bald zurück!", sorry: "Entschuldigung für die Unannehmlichkeiten.", working: "Arbeiten im Gange...", loginTitle: "Admin-Zugang", email: "E-Mail", password: "Passwort", login: "Anmelden", error: "Ungültige Anmeldedaten", success: "Zugang gewährt!" },
};

export default function MaintenancePage() {
  const { lang } = useI18n();
  const txt = maintenanceTexts[lang];
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) throw authError;

      // Check if user has admin role
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", data.user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (!roleData) {
        await supabase.auth.signOut();
        setError(txt.error);
        return;
      }

      // Admin confirmed — set bypass and reload
      localStorage.setItem("bypass_maintenance", "true");
      window.location.reload();
    } catch {
      setError(txt.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center max-w-md"
      >
        <div className="flex justify-center gap-3 mb-6">
          <Construction className="w-12 h-12 text-primary" />
          <button
            onClick={() => setShowLogin((v) => !v)}
            className="focus:outline-none hover:scale-110 transition-transform"
            aria-label="Admin access"
          >
            <KeyRound className="w-12 h-12 text-primary/60 hover:text-primary transition-colors" />
          </button>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">{txt.title}</h1>
        <p className="text-foreground/60 text-lg mb-2">{txt.desc}</p>
        <p className="text-foreground/70 text-sm">{txt.sorry}</p>

        <AnimatePresence>
          {showLogin && (
            <motion.form
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              onSubmit={handleLogin}
              className="mt-6 space-y-3 overflow-hidden"
            >
              <h2 className="text-foreground font-display font-semibold text-lg">{txt.loginTitle}</h2>
              <input
                type="email"
                required
                placeholder={txt.email}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-card border border-primary/20 rounded-md px-4 py-3 text-foreground text-sm focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none transition placeholder:text-foreground/25"
              />
              <input
                type="password"
                required
                placeholder={txt.password}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-card border border-primary/20 rounded-md px-4 py-3 text-foreground text-sm focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none transition placeholder:text-foreground/25"
              />
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary/20 hover:bg-primary/30 text-primary font-semibold py-3 rounded-md transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {txt.login}
              </button>
            </motion.form>
          )}
        </AnimatePresence>

        <div className="mt-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            {txt.working}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
